import { useDocuments } from '@/lib/documents';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { Card } from '@/components/ui/misc';
import { DocumentList } from '@/components/documents/DocumentList';

/** Every file in a phase: filed on the phase itself, its tasks or its deliverables. */
export function PhaseDocuments({ program, phaseId }: { program: Program; phaseId: string }) {
  const tracker = useTracker(program.id);
  const all = useDocuments(program.id);
  const taskIds = new Set(tracker.data?.tasks.filter((t) => t.phaseId === phaseId).map((t) => t.id));
  const delIds = new Set(tracker.data?.deliverables.filter((d) => d.phaseId === phaseId).map((d) => d.id));
  const docs = all.data?.filter(
    (d) =>
      d.phaseId === phaseId || (d.taskId && taskIds.has(d.taskId)) || (d.deliverableId && delIds.has(d.deliverableId)),
  );
  const isAdmin = program.capabilities.includes('plan.manage');
  const label = (id: string | null, kind: 'task' | 'deliverable') =>
    kind === 'task'
      ? tracker.data?.tasks.find((t) => t.id === id)?.title
      : tracker.data?.deliverables.find((d) => d.id === id)?.name;
  return (
    <Card className="px-4 py-2">
      <DocumentList
        program={program}
        docs={docs}
        loading={all.isLoading}
        uploadTarget={{ phaseId, label: 'this phase' }}
        canUpload={isAdmin}
        emptyText="No documents in this phase yet."
        showContext={(d) =>
          d.taskId
            ? ` · task: ${label(d.taskId, 'task')}`
            : d.deliverableId
              ? ` · deliverable: ${label(d.deliverableId, 'deliverable')}`
              : ''
        }
      />
    </Card>
  );
}
